import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, ArrowLeft, Loader2, TrendingUp, Users, AlertCircle, Target, BarChart3, MessageSquare, PieChart } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimulationResults = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [simulation, setSimulation] = useState(null);
  const [concept, setConcept] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSimulation();
    const interval = setInterval(() => {
      loadSimulation();
    }, 5000);

    return () => clearInterval(interval);
  }, [id]);

  const loadSimulation = async () => {
    try {
      const simRes = await axios.get(`${API}/simulations/${id}`, { withCredentials: true });
      setSimulation(simRes.data);

      if (simRes.data.product_concept_id) {
        const conceptRes = await axios.get(`${API}/concepts/${simRes.data.product_concept_id}`, { withCredentials: true });
        setConcept(conceptRes.data);
      }
    } catch (error) {
      console.error('Error loading simulation:', error);
      toast.error('Failed to load simulation results');
    } finally {
      setLoading(false);
    }
  };

  const getIntentColor = (score) => {
    if (score >= 4) return 'text-green-600';
    if (score >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getIntentLabel = (score) => {
    if (score >= 4.5) return 'Very High';
    if (score >= 4) return 'High';
    if (score >= 3) return 'Moderate';
    if (score >= 2) return 'Low';
    return 'Very Low';
  };

  const getBarColor = (score) => {
    if (score === 5) return '#10b981';
    if (score === 4) return '#84cc16';
    if (score === 3) return '#eab308';
    if (score === 2) return '#f97316';
    return '#ef4444';
  };

  const prepareDistributionData = () => {
    if (!simulation?.score_distribution) return [];
    return [
      { score: '1 (Low)', value: simulation.score_distribution['1'] || 0, numScore: 1 },
      { score: '2', value: simulation.score_distribution['2'] || 0, numScore: 2 },
      { score: '3', value: simulation.score_distribution['3'] || 0, numScore: 3 },
      { score: '4', value: simulation.score_distribution['4'] || 0, numScore: 4 },
      { score: '5 (High)', value: simulation.score_distribution['5'] || 0, numScore: 5 }
    ];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              IntentAI
            </span>
          </div>
          <Button onClick={() => navigate('/dashboard')} variant="outline" data-testid="back-to-dashboard-button" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="concept-name">
            {concept?.name || 'Loading...'}
          </h1>
          <p className="text-slate-600">{simulation?.num_simulations} AI consumer simulations</p>
        </div>

        {simulation?.status === 'running' && (
          <Card className="mb-8 border-yellow-200 bg-yellow-50" data-testid="running-status">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Loader2 className="w-6 h-6 animate-spin text-yellow-600" />
                <div className="flex-1">
                  <p className="font-semibold text-yellow-900 mb-2">Simulation Running...</p>
                  <Progress value={45} className="h-2" />
                </div>
              </div>
              <p className="text-sm text-yellow-700 mt-4">
                AI is simulating consumer reactions across diverse demographics. This typically takes 2-3 minutes for 100 simulations.
              </p>
            </CardContent>
          </Card>
        )}

        {simulation?.status === 'failed' && (
          <Card className="mb-8 border-red-200 bg-red-50" data-testid="failed-status">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-900">Simulation Failed</p>
                  <p className="text-sm text-red-700 mt-1">
                    The simulation encountered an error. Please ensure your OpenAI API key is configured correctly.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {simulation?.status === 'completed' && simulation.purchase_intent_score && (
          <Tabs defaultValue="overview" className="space-y-8">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="demographics">Demographics</TabsTrigger>
              <TabsTrigger value="responses">Sample Responses</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              {/* Purchase Intent Score */}
              <Card className="border-slate-200 shadow-xl" data-testid="intent-score-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-6 h-6 text-indigo-600" />
                    Purchase Intent Score
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <div className={`text-7xl font-bold mb-4 ${getIntentColor(simulation.purchase_intent_score)}`} data-testid="intent-score">
                      {simulation.purchase_intent_score.toFixed(1)}
                    </div>
                    <div className="text-2xl text-slate-600 mb-2">out of 5.0</div>
                    <div className="text-lg font-semibold text-slate-700">{getIntentLabel(simulation.purchase_intent_score)} Purchase Intent</div>
                    
                    {simulation.demographic_insights && (
                      <div className="mt-8 grid grid-cols-3 gap-6 max-w-2xl mx-auto">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-slate-800" data-testid="high-intent-percentage">
                            {simulation.demographic_insights.high_intent_percentage}%
                          </div>
                          <div className="text-sm text-slate-600 mt-1">Likely to Buy</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-slate-800" data-testid="total-simulations-count">
                            {simulation.demographic_insights.total_simulations}
                          </div>
                          <div className="text-sm text-slate-600 mt-1">Total Tests</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-slate-800" data-testid="low-intent-count">
                            {simulation.demographic_insights.low_intent_count}
                          </div>
                          <div className="text-sm text-slate-600 mt-1">Not Interested</div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Distribution Chart */}
              {simulation.score_distribution && (
                <Card className="border-slate-200 shadow-xl" data-testid="distribution-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-6 h-6 text-indigo-600" />
                      Score Distribution
                    </CardTitle>
                    <CardDescription>How consumers rated the product across the 1-5 scale</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div style={{ width: '100%', height: '350px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={prepareDistributionData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis 
                            dataKey="score" 
                            tick={{ fill: '#475569', fontSize: 14 }}
                            stroke="#94a3b8"
                          />
                          <YAxis 
                            label={{ value: 'Number of Consumers', angle: -90, position: 'insideLeft', style: { fill: '#475569' } }}
                            tick={{ fill: '#475569', fontSize: 14 }}
                            stroke="#94a3b8"
                          />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                            cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }}
                          />
                          <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={80}>
                            {prepareDistributionData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={getBarColor(entry.numScore)} stroke={getBarColor(entry.numScore)} strokeWidth={1} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Two Column Layout */}
              <div className="grid md:grid-cols-2 gap-8">
                {simulation.emotional_drivers && simulation.emotional_drivers.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                        Key Emotional Drivers
                      </CardTitle>
                      <CardDescription>What motivates buyers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3" data-testid="emotional-drivers-list">
                        {simulation.emotional_drivers.map((driver, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100" data-testid={`driver-${idx}`}>
                            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                              {idx + 1}
                            </div>
                            <span className="font-medium text-slate-800">{driver}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {simulation.objections && simulation.objections.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                        Top Objections
                      </CardTitle>
                      <CardDescription>Concerns from hesitant consumers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3" data-testid="objections-list">
                        {simulation.objections.map((objection, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100" data-testid={`objection-${idx}`}>
                            <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">
                              {idx + 1}
                            </div>
                            <span className="font-medium text-slate-800">{objection}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Market Segments */}
              {simulation.market_segments && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-6 h-6 text-purple-600" />
                      Predicted Market Segments
                    </CardTitle>
                    <CardDescription>Demographics with highest purchase intent</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-8">
                      {simulation.market_segments.top_age_groups && simulation.market_segments.top_age_groups.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4">Top Age Groups</h4>
                          <div className="space-y-2" data-testid="age-groups-list">
                            {simulation.market_segments.top_age_groups.map(([age, count], idx) => (
                              <div key={idx} className="flex justify-between items-center p-2 bg-purple-50 rounded" data-testid={`age-group-${idx}`}>
                                <span className="font-medium text-slate-800">{age}</span>
                                <span className="text-purple-600 font-semibold">{count} high-intent</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {simulation.market_segments.top_income_groups && simulation.market_segments.top_income_groups.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4">Top Income Groups</h4>
                          <div className="space-y-2" data-testid="income-groups-list">
                            {simulation.market_segments.top_income_groups.map(([income, count], idx) => (
                              <div key={idx} className="flex justify-between items-center p-2 bg-purple-50 rounded" data-testid={`income-group-${idx}`}>
                                <span className="font-medium text-slate-800">{income}</span>
                                <span className="text-purple-600 font-semibold">{count} high-intent</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Demographics Tab */}
            <TabsContent value="demographics" className="space-y-8">
              {simulation.demographic_breakdown && (
                <>
                  <Card className="border-slate-200 shadow-xl">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-6 h-6 text-indigo-600" />
                        Breakdown by Age Group
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        {Object.entries(simulation.demographic_breakdown.by_age).map(([age, data]) => (
                          <div key={age} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-semibold text-slate-800">{age}</span>
                              <span className="text-sm text-slate-600">{data.count} consumers</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-2xl font-bold text-indigo-600">{data.average_score.toFixed(1)}</span>
                              <span className="text-sm font-medium text-green-600">{data.high_intent_pct}% likely to buy</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid md:grid-cols-2 gap-8">
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle>By Gender</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {Object.entries(simulation.demographic_breakdown.by_gender).map(([gender, data]) => (
                            <div key={gender} className="p-4 bg-slate-50 rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-semibold text-slate-800">{gender}</span>
                                <span className="text-xl font-bold text-indigo-600">{data.average_score.toFixed(1)}</span>
                              </div>
                              <div className="text-sm text-slate-600">{data.high_intent_pct}% high intent • {data.count} consumers</div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle>By Location</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {Object.entries(simulation.demographic_breakdown.by_location).map(([location, data]) => (
                            <div key={location} className="p-4 bg-slate-50 rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-semibold text-slate-800">{location}</span>
                                <span className="text-xl font-bold text-indigo-600">{data.average_score.toFixed(1)}</span>
                              </div>
                              <div className="text-sm text-slate-600">{data.high_intent_pct}% high intent • {data.count} consumers</div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="border-slate-200 shadow-xl">
                    <CardHeader>
                      <CardTitle>By Income Level</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-4">
                        {Object.entries(simulation.demographic_breakdown.by_income).map(([income, data]) => (
                          <div key={income} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="mb-2">
                              <span className="font-semibold text-slate-800 text-sm">{income}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-2xl font-bold text-indigo-600">{data.average_score.toFixed(1)}</span>
                              <span className="text-sm font-medium text-green-600">{data.high_intent_pct}%</span>
                            </div>
                            <div className="text-xs text-slate-600 mt-1">{data.count} consumers</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Sample Responses Tab */}
            <TabsContent value="responses" className="space-y-8">
              {simulation.sample_responses && simulation.sample_responses.length > 0 && (
                <Card className="border-slate-200 shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-6 h-6 text-indigo-600" />
                      Sample Consumer Responses
                    </CardTitle>
                    <CardDescription>Representative feedback from different intent levels</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {simulation.sample_responses.map((sample, idx) => {
                        const categoryColors = {
                          'High Intent': 'border-green-200 bg-green-50',
                          'Moderate Intent': 'border-yellow-200 bg-yellow-50',
                          'Low Intent': 'border-red-200 bg-red-50'
                        };
                        return (
                          <div key={idx} className={`p-4 rounded-lg border-2 ${categoryColors[sample.category]}`}>
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-semibold text-slate-800">{sample.category}</span>
                              <span className="text-sm font-bold text-indigo-600">Score: {sample.score.toFixed(1)}/5</span>
                            </div>
                            <p className="text-slate-700 mb-2 italic">"{sample.response}"</p>
                            <p className="text-xs text-slate-500">{sample.demographic}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default SimulationResults;